/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import {
  Button,
  ButtonVariety,
  IconDelete,
  LinkStandalone,
  MessageInline,
  Modal,
  Text,
} from '@sonarsource/echoes-react';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

const JIRA_CONNECTED_APPS_URL = 'https://id.atlassian.com/manage-profile/apps';

interface Props {
  isPending: boolean;
  onDisconnect: () => void;
}

export function OrganizationJiraUnlinkButton(props: Readonly<Props>) {
  const { onDisconnect, isPending } = props;
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { formatMessage } = useIntl();

  const handleDisconnect = useCallback(() => {
    setIsDeleteModalOpen(false);
    onDisconnect();
  }, [onDisconnect]);

  return (
    <>
      <Button
        ariaLabel={formatMessage({ id: 'organization.jira.binding.disconnect.label' })}
        isDisabled={isPending}
        isLoading={isPending}
        name="disconnect"
        onClick={() => {
          setIsDeleteModalOpen(true);
        }}
        type="reset"
        variety={ButtonVariety.Default}
      >
        <IconDelete />
      </Button>
      <Modal
        content={
          <>
            <FormattedMessage
              id="organization.jira.binding.disconnect.modal.description.line1"
              values={{
                p: (text) => (
                  <Text as="p" className="sw-mb-4">
                    {text}
                  </Text>
                ),
              }}
            />
            <Text as="p" className="sw-mb-4">
              <FormattedMessage id="organization.jira.binding.disconnect.modal.description.line2" />
            </Text>
            <ul className="sw-mb-4 sw-list-disc sw-pl-4 sw-flex sw-flex-col sw-gap-1">
              <li>
                <FormattedMessage id="organization.jira.binding.disconnect.modal.description.bullet1" />
              </li>
              <li>
                <FormattedMessage id="organization.jira.binding.disconnect.modal.description.bullet2" />
              </li>
              <li>
                <FormattedMessage id="organization.jira.binding.disconnect.modal.description.bullet3" />
              </li>
            </ul>
            <MessageInline as="div" className="sw-mb-4" variety="danger">
              <FormattedMessage id="organization.jira.binding.disconnect.modal.description.warning" />
            </MessageInline>
          </>
        }
        footerLink={
          <LinkStandalone enableOpenInNewTab to={JIRA_CONNECTED_APPS_URL}>
            <FormattedMessage id="organization.jira.binding.disconnect.modal.link" />
          </LinkStandalone>
        }
        isOpen={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        primaryButton={
          <Button onClick={handleDisconnect} variety={ButtonVariety.Danger}>
            <FormattedMessage id="delete" />
          </Button>
        }
        secondaryButton={
          <Button
            onClick={() => {
              setIsDeleteModalOpen(false);
            }}
          >
            {formatMessage({ id: 'cancel' })}
          </Button>
        }
        title={formatMessage({ id: 'organization.jira.binding.disconnect.modal.title' })}
      />
    </>
  );
}
